import type { FetchClient, RequestOptions } from '../../../lib/http/http.types';
import { toQueryString } from '../../../lib/url/urlUtils';
import type {
  FilterState,
  PaginationMeta,
  TableColumn,
} from '../../../components/ui/tableTemplate/TableTemplateTypes';
import type { LabeledCode } from '../../../lib/labeledCode';
import {
  appendFilterParams,
  resolveDbSortBy,
  mapApiRows,
} from '../../../components/ui/tableTemplate/tableUtils';
import type { EmployeeRow } from '../employeeTypes';
import EmployeeEndpoints from './employeeEndpoints';
import { mapEmployeeDetailRow } from './mapEmployeeDetailRow';
import { capitalizeLabel, capitalizeLabels } from '../../../lib/capitalize';

export type EmployeeFilterOptions = {
  sexe: LabeledCode[];
  statutfamilial: LabeledCode[];
  relocatedstaff: LabeledCode[];
  typecontrat: LabeledCode[];
  typecreation: LabeledCode[];
  contratencours: LabeledCode[];
  periodeessai: LabeledCode[];
  pays: LabeledCode[];
  codeterrain: LabeledCode[];
  fonction: LabeledCode[];
  codegrillesalaire: LabeledCode[];
  /**
   * Installation-level "Contract activation" option (`global.contratclosing='O'`),
   * uniform per ONG. Drives the activated/not-activated split of the contract-status
   * indicator. `false` when the backend reports anything other than `'O'` (or omits it).
   */
  contractActivationEnabled: boolean;
};

/** Raw wire shape of the filter-options endpoint (backend adds `contratclosing`). */
type RawEmployeeFilterOptions = Omit<EmployeeFilterOptions, 'contractActivationEnabled'> & {
  contratclosing?: string | null;
};

export type GetEmployeesParams = {
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  filters?: FilterState<EmployeeRow>;
  columns?: TableColumn<EmployeeRow>[];
  page?: number;
  limit?: number;
};

/**API STRATEGY: Server-Side Sorting + Filtering
 * Translates SortState and FilterState into URL parameters.
 * * Logic:
 * - sortBy: keyof EmployeeRow (mapped to string)
 * - sortOrder: 'asc' | 'desc'
 * - filters: FilterState<EmployeeRow> → <key>=<value> (database column names)
 */
export type Response<T> = {
  data: T[];
  meta: PaginationMeta;
};

export function EmployeeApiFactory(client: FetchClient) {
  return {
    getById: async (id: string, opts?: RequestOptions): Promise<EmployeeRow> => {
      const raw = await client.get<Record<string, unknown>>(
        `${EmployeeEndpoints.BASE}/${id}`,
        opts,
      );
      return mapEmployeeDetailRow(raw);
    },

    getAll: (
      params?: GetEmployeesParams,
      opts?: RequestOptions,
    ): Promise<Response<EmployeeRow>> => {
      const qs = new URLSearchParams();
      const columns = params?.columns ?? [];

      // Sort — translate the frontend key to its database column name before sending.
      // e.g. last_name → nom (defined via databaseName in the column definition)
      if (params?.sortBy) {
        qs.set(EmployeeEndpoints.PARAMS.SORT_BY, resolveDbSortBy(params.sortBy, columns));
      }
      if (params?.sortOrder) qs.set(EmployeeEndpoints.PARAMS.SORT_ORDER, params.sortOrder);

      // Filters — each key is translated to its database column name and serialized
      // according to its filter kind (text, date, boolean, numeric, list).
      if (params?.filters && params?.columns) {
        appendFilterParams(qs, params.filters, params.columns);
      }

      // Pagination — omitted when not provided so the server applies its own defaults.
      if (params?.page != null) qs.set(EmployeeEndpoints.PARAMS.PAGE, String(params.page));
      if (params?.limit != null) qs.set(EmployeeEndpoints.PARAMS.LIMIT, String(params.limit));

      const query = toQueryString(qs);
      // e.g. /employees?sortBy=nom&sortOrder=asc&lastName[like]=Dupont&fieldCode[in]=CF_BAG1,CF_BAN1&page=2&limit=25

      return client
        .get<
          Response<Record<string, unknown>>
        >(`${EmployeeEndpoints.BASE}${query ? `?${query}` : ''}`, opts)
        .then(response => ({
          ...response,
          data: mapApiRows<EmployeeRow>(response.data, columns).map(row => {
            // `statutactive` is preserved by mapApiRows (not a column) — normalize it
            // to the row's `is_active` boolean so the contract-status indicator can
            // read a representation-agnostic flag ('O'/1 → activated).
            const statutactive = (row as { statutactive?: unknown }).statutactive;
            return {
              ...row,
              country: capitalizeLabel(row.country as LabeledCode | undefined),
              marital_status: capitalizeLabel(row.marital_status as LabeledCode | undefined),
              is_active: statutactive === 'O' || statutactive === 1 ? true : false,
            };
          }),
        }));
    },

    getFilterOptions: (lang?: string, opts?: RequestOptions): Promise<EmployeeFilterOptions> => {
      const qs = new URLSearchParams();
      if (lang) qs.set('lang', lang);
      const query = toQueryString(qs);
      return client
        .get<RawEmployeeFilterOptions>(
          `${EmployeeEndpoints.FILTER_OPTIONS}${query ? `?${query}` : ''}`,
          opts,
        )
        .then(filterOptions => ({
          sexe: capitalizeLabels(filterOptions.sexe),
          statutfamilial: capitalizeLabels(filterOptions.statutfamilial),
          relocatedstaff: capitalizeLabels(filterOptions.relocatedstaff),
          typecontrat: capitalizeLabels(filterOptions.typecontrat),
          typecreation: capitalizeLabels(filterOptions.typecreation),
          contratencours: capitalizeLabels(filterOptions.contratencours),
          periodeessai: capitalizeLabels(filterOptions.periodeessai),
          pays: capitalizeLabels(filterOptions.pays),
          codeterrain: capitalizeLabels(filterOptions.codeterrain),
          fonction: capitalizeLabels(filterOptions.fonction),
          codegrillesalaire: capitalizeLabels(filterOptions.codegrillesalaire),
          // Driven solely by the per-ONG backend option (`global.contratclosing`):
          // 'O' → activation-aware (3-state) mode; anything else / absent → the
          // status collapses to the current / not-current (`none`) pair.
          contractActivationEnabled: filterOptions.contratclosing === 'O',
        }));
    },
  };
}
